import React from 'react'
import { Link } from 'react-router-dom'
import { Shield, Lock, Eye, FileCheck, CheckCircle, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { Button } from '../components/ui/Button'

export const SecurityPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-6xl mb-6">
              Security & Compliance
            </h1>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              Your data security and privacy are our top priorities. We employ industry-leading security measures 
              to protect your information.
            </p>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Encryption */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                End-to-End Encryption
              </h3>
              <p className="text-muted-foreground">
                All data is encrypted in transit (TLS 1.3) and at rest (AES-256) to ensure maximum security.
              </p>
            </div>

            {/* Access Controls */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Eye className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Access Controls
              </h3>
              <p className="text-muted-foreground">
                Role-based access with multi-factor authentication and regular security audits.
              </p>
            </div>

            {/* Compliance */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <FileCheck className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                SOC 2 Certified
              </h3>
              <p className="text-muted-foreground">
                Our infrastructure is SOC 2 Type II certified, ensuring the highest standards of security and compliance.
              </p>
            </div>

            {/* Regular Audits */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Regular Audits
              </h3>
              <p className="text-muted-foreground">
                Third-party security audits and penetration testing ensure continuous protection.
              </p>
            </div>

            {/* Monitoring */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                24/7 Monitoring
              </h3>
              <p className="text-muted-foreground">
                Continuous security monitoring and rapid incident response to protect your data.
              </p>
            </div>

            {/* Data Privacy */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Data Privacy
              </h3>
              <p className="text-muted-foreground">
                We never sell your data. Your information is used solely to provide our services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-muted/50 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Compliance & Certifications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">SOC 2 Type II</h3>
                <p className="text-muted-foreground">
                  Certified for security, availability, processing integrity, confidentiality, and privacy.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">IRS Compliance</h3>
                <p className="text-muted-foreground">
                  Full compliance with IRS regulations for 1031 exchanges and qualified intermediary services.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">GDPR Ready</h3>
                <p className="text-muted-foreground">
                  Compliant with GDPR and other international data protection regulations.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">KYC/AML</h3>
                <p className="text-muted-foreground">
                  Know Your Customer and Anti-Money Laundering compliance for all transactions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl mb-4">
            Trust Your Exchange to Secure Hands
          </h2>
          <p className="mt-4 text-lg text-primary-foreground/90 mb-8">
            Get started with Open1031 today and experience enterprise-grade security.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('openChat'))
              }}
            >
              Get Started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Link to="/contact">
              <Button variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

